// src/utils/buyerActivity.js
// Pure, Firebase-free helpers for the Buyer Lead Tracker: activity types,
// A/B/C rating, and follow-up due/sort logic. Dates are ISO "YYYY-MM-DD"
// strings (lexicographic order == chronological), so `today` is injected.
import { addDaysYMD, chicagoTodayYMD, chicagoYMD } from "./chicagoTime";

export const ACTIVITY_TYPES = ["Call", "Text", "Email", "Note", "Meeting"];
export const RATING_OPTIONS = ["A", "B", "C"];

const RATING_COLORS = {
  A: "bg-red-100 text-red-700",
  B: "bg-amber-100 text-amber-700",
  C: "bg-slate-100 text-slate-600",
};
export const getRatingColor = (r) => RATING_COLORS[r] || "";

export function followUpStatus(lead, todayIso) {
  const d = lead?.nextFollowUp;
  if (!d) return "none";
  if (d < todayIso) return "overdue";
  if (d === todayIso) return "today";
  return "upcoming";
}

export function isFollowUpDue(lead, todayIso) {
  const s = followUpStatus(lead, todayIso);
  return s === "overdue" || s === "today";
}

// Soonest nextFollowUp first; leads with no date sort last.
export function sortByFollowUp(leads = []) {
  return [...leads].sort((a, b) => {
    const da = a?.nextFollowUp || "";
    const db = b?.nextFollowUp || "";
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.localeCompare(db);
  });
}

// id + at are injected by the caller so this stays pure/testable.
export function newActivity({ type, note = "", by = "", byName = "", id, at }) {
  return { id, type, note: (note || "").trim(), by, byName, at };
}

// Today in Minnesota (or `fromIso`) + `days`, as ISO "YYYY-MM-DD", used by the
// follow-up snooze presets (Tomorrow / +3 / +1 week).
export function addDaysIso(days, fromIso) {
  return addDaysYMD(fromIso || chicagoTodayYMD(), days);
}

// ---- Editing / deleting a logged activity --------------------------------
// Activities are one array on the lead doc, so an edit or delete rewrites the
// array (the Firestore side does that inside a transaction). Entries logged
// since the tracker shipped carry an `id`; a few older ones may not, so the
// match falls back to the (at, type, note) triple.
export function sameActivity(a, b) {
  if (!a || !b) return false;
  if (a.id != null && b.id != null) return String(a.id) === String(b.id);
  return (
    Number(a.at || 0) === Number(b.at || 0) &&
    String(a.type || "") === String(b.type || "") &&
    String(a.note || "") === String(b.note || "")
  );
}

export function removeActivity(activities, target) {
  return (activities || []).filter((a) => !sameActivity(a, target));
}

// Only what was said (type + note) changes; who logged it and when stay so the
// timeline keeps its place. `editedAt` lets the timeline show "(edited)".
export function replaceActivity(activities, target, patch = {}, editedAt) {
  return (activities || []).map((a) => {
    if (!sameActivity(a, target)) return a;
    const next = { ...a, editedAt };
    if (patch.type) next.type = patch.type;
    if (patch.note != null) next.note = String(patch.note).trim();
    // Who made the edit, so a rewritten entry is never anonymous.
    if (patch.editedBy) next.editedBy = patch.editedBy;
    if (patch.editedByName) next.editedByName = patch.editedByName;
    return next;
  });
}

// The Minnesota "YYYY-MM-DD" stamp logBuyerActivity writes to lastContacted
// (it used to be the UTC day, so an evening call was stamped tomorrow).
export function activityDateIso(at) {
  return chicagoYMD(Number(at) || 0);
}

// Snooze / dismiss leave "Note" breadcrumbs on the timeline that are NOT a
// contact (FollowUpsDueWidget never stamps lastContacted for them), so they
// don't count here either.
const BREADCRUMB_NOTE = /^(Snoozed follow-up|Dismissed follow-up)/;
export const isFollowUpBreadcrumb = (a) =>
  String(a?.type || "") === "Note" && BREADCRUMB_NOTE.test(String(a?.note || ""));

export function latestContactIso(activities) {
  const latest = (activities || [])
    .filter((a) => !isFollowUpBreadcrumb(a))
    .reduce((max, a) => Math.max(max, Number(a?.at) || 0), 0);
  return latest ? activityDateIso(latest) : "";
}

// After deleting `deleted`, what should lead.lastContacted be? Null means
// "leave it": only the entry that set the stamp gets to move it, so deleting
// an old note never rewinds a newer real contact.
export function lastContactedAfterDelete(lead, deleted, remaining) {
  const current = lead?.lastContacted || "";
  if (!current || current !== activityDateIso(deleted?.at)) return null;
  return latestContactIso(remaining);
}
